import type { PromoLine } from './types';

/**
 * A DISCOUNT IS NOT A QUESTION
 * ============================
 *
 * MP has no separate place to keep promos: a promo is a Product_Option_Price
 * with a negative price, filed in an option group like "Promo codes" that
 * looks to the browser exactly like "Which campus" or "T-shirt size". So the
 * sheet drew it as a list to tick, and two things went wrong at once — a
 * volunteer on a free seat was offered $2 off it, and a student could tick a
 * discount somebody else's rule had earned, or miss the one that was theirs.
 *
 * Who gets what off is the server's answer, decided against MP's eligibility
 * rules at submit and previewed before. There is nothing here for a person to
 * choose, so the shelf is never drawn at all.
 */

/**
 * A group that is nothing but money off.
 *
 * The sign of the prices is the only signal MP gives — there is no column
 * saying "these are promos" — and this is the same reading the staff-side
 * ways-in panel uses. Every option BELOW zero, so a group of free choices is
 * still a question worth asking.
 */
export function isDiscountShelf(g: { options: { price: number }[] }): boolean {
  return g.options.length > 0 && g.options.every((o) => o.price < 0);
}

/**
 * WHAT THIS PERSON ACTUALLY PAYS.
 *
 * The sticker price less the discounts the server already decided are theirs.
 * Discounts stack in MP when their rules say so, so this is a sum and not a
 * pick — a staff member's student takes $2 off and whatever else applies on
 * top of it — and it floors at nothing, because a registration can be brought
 * to free but never past it.
 *
 * A preview. Submit re-decides against today's rules and writes its own
 * number; if that differs, the sheet says so rather than charging quietly.
 */
export function netPrice(price: number, promos: PromoLine[] | undefined): number {
  if (!promos?.length) return price;
  return Math.max(
    0,
    promos.reduce((n, p) => n + p.price, price)
  );
}
